import React from "react";
import { motion } from "framer-motion";
import { Brain, Code, Rocket, Sparkles } from "lucide-react";
import { Section, SectionHeading } from "./_shared";

const PILLARS = [
  { icon: Brain, title: "Backend Architecture", body: "Scalable microservices and RESTful APIs with Java, Spring Boot, and clean layered design." },
  { icon: Code, title: "Systems Thinking", body: "200+ DSA problems across LeetCode, GeeksforGeeks and InterviewBit. Algorithms are instinct, not chore." },
  { icon: Rocket, title: "Performance", body: "Query optimization and service-layer tuning that consistently cuts response times by 25-35%." },
  { icon: Sparkles, title: "Craft", body: "Obsessed with the details: Spring Security, clean exception handling, well-documented APIs." },
];

const About = ({ hero = {} }) => {
  return (
    <Section id="about">
      <SectionHeading
        index="01"
        label="About"
        title="The shape of what I do."
        description={hero.tagline || "I build AI systems at the intersection of multimodal models, data pipelines and human-centric interfaces."}
        testId="about-heading"
      />

      <div className="grid lg:grid-cols-12 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 glass glass-hover rounded-3xl p-8 md:p-10"
          data-testid="about-bio-card"
        >
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300 mb-4">Bio</div>
          <p className="text-slate-300 leading-relaxed text-base md:text-lg whitespace-pre-line">
            {hero.summary}
          </p>
          {hero.currently_text && (
            <p className="mt-5 text-slate-400 leading-relaxed">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300 block mb-2">Currently</span>
              {hero.currently_text}
            </p>
          )}
        </motion.div>

        <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1 + i * 0.08 }}
              className="glass glass-hover rounded-2xl p-5"
              data-testid={`about-pillar-${i}`}
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 grid place-items-center mb-3">
                <p.icon size={18} className="text-cyan-300" />
              </div>
              <div className="font-display text-white text-base font-medium">{p.title}</div>
              <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default About;
